import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Button,
    Box,
    Container,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormHelperText,
    FormControlLabel,
    Switch,
    CircularProgress,
    Slider,
    Stack,
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
// Image cropping imports
import Cropper from 'react-easy-crop';
import {
    registerOcrBeforeUnloadGuard,
    cancelOcrOnEditorTeardown,
    cancelRagUploadWithHandler,
    confirmOcrCancelBeforeLeave as confirmOcrCancelBeforeLeaveUtil
} from '../utils/promptEditorUtils/ocrUtils';
// Other utils
import {
    loadRagDocuments as loadRagDocumentsUtil,
    handleRagFileUpload as handleRagFileUploadUtil,
    submitRagUrlForCurrentPrompt,
    openClearRagDocumentsDialog,
    confirmClearRagDocuments as confirmClearRagDocumentsUtil,
    cancelClearRagDocuments as cancelClearRagDocumentsUtil,
    openDeleteRagDocumentDialog,
    confirmDeleteRagDocument as confirmDeleteRagDocumentUtil,
    cancelDeleteRagDocument as cancelDeleteRagDocumentUtil,
    handleDownloadOcrDocument as handleDownloadOcrDocumentUtil,
    handleRagToggle as handleRagToggleUtil,
    formatUrlForDisplay
} from '../utils/promptEditorUtils/ragUtils';
import {
    ChatbotDropdown
} from '../utils/chatbotDropdownUtils';
import {generateUniqueChatbotId, isStandardizedChatbotId} from '../utils/globalCons';
import getAvatarDisplayUrl, {
    refreshCurrentAvatarFromServer as refreshCurrentAvatarFromServerUtil,
    uploadAvatarAndPersist as uploadAvatarAndPersistUtil,
    clearAvatarAndPersist,
    prepareAvatarForPromptSubmit,
    handleAvatarCropComplete,
    applyAvatarCrop,
    cancelAvatarCrop
} from '../utils/promptEditorUtils/avatarUtils';

// =============================================
// PROMPT EDITOR COMPONENT (FOR PROFESSORS) - V2
// This component allows creating new prompts and editing existing ones,
// with a two-step dropdown selection: Language -> Chatbot.
// =============================================

const PromptEditor = () => {
    const ADD_NEW_LEVEL_OPTION = '__ADD_NEW_LEVEL__';
    const CREATE_NEW_PROMPT_OPTION = '__CREATE_NEW_PROMPT__';
    // --- State Variables (all useState hooks must be here) ---
    const [allPrompts, setAllPrompts] = useState({});
    const [allTemplates, setAllTemplates] = useState([]);
    const [aiLevels, setAiLevels] = useState({});
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [selectedPromptTitle, setSelectedPromptTitle] = useState('');
    const [promptSearchTerm, setPromptSearchTerm] = useState('');
    const [formData, setFormData] = useState({
        title: '',
        name: '',
        language: '',
        level: '',
        voice: 'alloy',
        initialText: '',
        prompt: '',
        instructions: '',
        avatar_url: '',
        use_rag: false
    });
    // Available voices for selection
    const availableVoices = [
        { value: 'alloy', label: 'Alloy (Default Male)' },
        { value: 'ash', label: 'Ash (Male)' },
        { value: 'ballad', label: 'Ballad (Male)' },
        { value: 'coral', label: 'Coral (Female)' },
        { value: 'echo', label: 'Echo (Male)' },
        { value: 'fable', label: 'Fable (Male)' },
        { value: 'onyx', label: 'Onyx (Male)' },
        { value: 'nova', label: 'Nova (Female)' },
        { value: 'sage', label: 'Sage (Female)' },
        { value: 'shimmer', label: 'Shimmer (Female)' },
        { value: 'verse', label: 'Verse (Male)' },
    ];
    const [statusMessage, setStatusMessage] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [pendingAvatarDelete, setPendingAvatarDelete] = useState(false); // Mark avatar for deletion
    const [copiedFromChatbotTitle, setCopiedFromChatbotTitle] = useState('');
    const [copiedFromSourceChatbotTitle, setCopiedFromSourceChatbotTitle] = useState('');
    const [copiedSourceHasAvatar, setCopiedSourceHasAvatar] = useState(false);

    // These two lines MUST be here at the top with other useState calls
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [promptToDelete, setPromptToDelete] = useState(null); // Holds the name of the prompt to be deleted
    const [openClearDocsDialog, setOpenClearDocsDialog] = useState(false);
    const [openDeleteDocDialog, setOpenDeleteDocDialog] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    const [openDeleteAvatarDialog, setOpenDeleteAvatarDialog] = useState(false);
    const [openAddLevelDialog, setOpenAddLevelDialog] = useState(false);
    const [openEditLevelDialog, setOpenEditLevelDialog] = useState(false);
    const [openDeleteLevelDialog, setOpenDeleteLevelDialog] = useState(false);
    const [newLevelValue, setNewLevelValue] = useState('');
    const [levelToEdit, setLevelToEdit] = useState('');
    const [editedLevelValue, setEditedLevelValue] = useState('');
    const [levelToDelete, setLevelToDelete] = useState('');
    const [addingLevel, setAddingLevel] = useState(false);
    const [editingLevel, setEditingLevel] = useState(false);
    const [deletingLevel, setDeletingLevel] = useState(false);
    const [ragDisabledNotice, setRagDisabledNotice] = useState(false);
    // Image cropping state
    const [showCropDialog, setShowCropDialog] = useState(false);
    const [cropImageSrc, setCropImageSrc] = useState(null);
    const [pendingImageFile, setPendingImageFile] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // RAG document management state
    const [ragDocuments, setRagDocuments] = useState([]);
    const [ragUploadLoading, setRagUploadLoading] = useState(false);
    const [ragOcrStatusMessage, setRagOcrStatusMessage] = useState('');
    const [ragUploadCancelHandler, setRagUploadCancelHandler] = useState(null);
    const [ragUploadUrl, setRagUploadUrl] = useState('');
    const [ragUrlLoading, setRagUrlLoading] = useState(false);
    const [deletingDocFilename, setDeletingDocFilename] = useState(null);
    const [downloadingOcrFilename, setDownloadingOcrFilename] = useState(null);

    const refreshCurrentAvatarFromServer = async (chatbotTitle, fallbackUrl = '') => {
        await refreshCurrentAvatarFromServerUtil({
            chatbotTitle,
            fallbackUrl,
            setFormData
        });
    };

    // Reference to RAG file input for auto-upload on toggle
    const ragFileInputRef = useRef(null);
    const selectedPromptTitleRef = useRef(selectedPromptTitle);
    const isEditModeRef = useRef(isEditMode);
    const ragUploadLoadingRef = useRef(ragUploadLoading);
    const ragUploadCancelHandlerRef = useRef(ragUploadCancelHandler);

    useEffect(() => {
        selectedPromptTitleRef.current = selectedPromptTitle;
    }, [selectedPromptTitle]);

    useEffect(() => {
        isEditModeRef.current = isEditMode;
    }, [isEditMode]);

    useEffect(() => {
        ragUploadLoadingRef.current = ragUploadLoading;
    }, [ragUploadLoading]);

    useEffect(() => {
        ragUploadCancelHandlerRef.current = ragUploadCancelHandler;
    }, [ragUploadCancelHandler]);

    useEffect(() => {
        return registerOcrBeforeUnloadGuard({ ragUploadLoadingRef });
    }, []);

    useEffect(() => {
        return () => {
            cancelOcrOnEditorTeardown({
                ragUploadLoadingRef,
                selectedPromptTitleRef,
                ragUploadCancelHandlerRef
            });
        };
    }, []);


    // --- Helper Functions (like getUniqueLanguages) ---
    const getUniqueLanguages = (configs) => {
        const languages = Object.values(configs).map(config => config.language);
        return [...new Set(languages)].sort();
    };

    const selectedChatbotDisplayName = formData.name || allPrompts[selectedPromptTitle]?.name || selectedPromptTitle;
    const promptToDeleteDisplayName = (promptToDelete && (allPrompts[promptToDelete]?.name || promptToDelete)) || '';


    // =============================================
    // FETCH PROMPTS FUNCTION (Refactored: Outside useEffect, using useCallback)
    // This is now a standalone function that can be called by useEffect or confirmDelete
    // =============================================
    const fetchPrompts = useCallback(async () => {
        try {
            const response = await axios.get(`/api/data`);
            setAllPrompts(response.data);
            setAvailableLanguages(getUniqueLanguages(response.data));
        } catch (error) {
            console.error("Failed to fetch prompts:", error);
            setStatusMessage("Error: Could not load existing prompts.");
        }
    }, []); // Empty dependency array if getUniqueLanguages is also stable

    const fetchTemplates = useCallback(async () => {
        try {
            const response = await axios.get('/api/templates');
            const templatesData = Array.isArray(response.data) ? response.data : [];
            setAllTemplates(templatesData);
        } catch (error) {
            console.error('Failed to fetch templates:', error);
            setStatusMessage('Error: Could not load templates.');
        }
    }, []);

    const fetchAiLevels = useCallback(async () => {
        try {
            const response = await axios.get('/api/levels');
            const levelsData = response.data && typeof response.data === 'object' ? response.data : {};
            setAiLevels(levelsData);
        } catch (error) {
            console.error('Failed to fetch AI levels:', error);
            setStatusMessage('Error: Could not load AI levels.');
        }
    }, []);


    // --- useEffect to call fetchPrompts on component mount ---
    // This useEffect now just calls the defined fetchPrompts function
    useEffect(() => {
        fetchPrompts();
        fetchAiLevels();
        fetchTemplates();
    }, [fetchPrompts, fetchAiLevels, fetchTemplates]); // Add dependencies to satisfy ESLint hooks rule


    // --- Filtered Prompts (derived state) ---
    const getFilteredPrompts = () => {
        if (!selectedLanguage) return {};
        return Object.fromEntries(
            Object.entries(allPrompts).filter(([, config]) =>
                config.language === selectedLanguage
            )
        );
    };

    const getAvailableLevels = () => {
        if (!selectedLanguage) return [];
        const levels = Array.isArray(aiLevels[selectedLanguage]) ? aiLevels[selectedLanguage] : [];
        const cleanedLevels = levels
            .map((level) => (level || '').trim())
            .filter((level) => level.length > 0);

        if (formData.level && !cleanedLevels.some((level) => level.toLowerCase() === formData.level.toLowerCase())) {
            cleanedLevels.push(formData.level);
        }

        return [...new Set(cleanedLevels)].sort((a, b) =>
            a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
        );
    };


    // =============================================
    // DELETE PROMPT FUNCTIONS
    // These functions MUST be here, after state and fetchPrompts
    // =============================================
    const handleDeletePrompt = (promptName) => { // This name matches the one in your JSX now
        setPromptToDelete(promptName);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        setOpenDeleteDialog(false); // Close the dialog immediately
        if (!promptToDelete) return; // promptToDelete is now defined

        const chatbotName = allPrompts[promptToDelete]?.name || promptToDelete;
        try {
            const response = await axios.post('/api/delete_prompt', {
                prompt_name: promptToDelete,
            });

            if (response.data.success) {
                setStatusMessage(`Chatbot "${chatbotName}" deleted successfully!`); // Use setStatusMessage for feedback
                // Update PromptEditor's state to reflect deletion
                if (selectedPromptTitle === promptToDelete) { // Use selectedPromptTitle here
                    setSelectedPromptTitle(''); // Clear the editor's selected prompt
                    setIsEditMode(false); // Exit edit mode
                    // Generate a new unique identifier for the next prompt
                    const uniqueId = generateUniqueChatbotId();
                    setFormData({
                        title: uniqueId,
                        name: '',
                        language: selectedLanguage, // Use selectedLanguage for consistency
                        level: '',
                        voice: 'alloy', // Voice defaults to 'alloy'
                        initialText: '',
                        prompt: '',
                        instructions: '',
                        avatar_url: ''
                    });
                }
                fetchPrompts(); // Call PromptEditor's fetchPrompts to refresh the dropdown/list
            } else {
                setStatusMessage(`Error deleting prompt: ${response.data.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error deleting prompt:', error);
            setStatusMessage('Failed to delete prompt. Please check console for details.');
        } finally {
            setPromptToDelete(null); // Clear the prompt to delete
        }
    };

    const cancelDelete = () => {
        setOpenDeleteDialog(false);
        setPromptToDelete(null);
    };

    const uploadAvatarAndPersist = async (file) => {
        await uploadAvatarAndPersistUtil({
            file,
            selectedPromptTitle,
            formData,
            setFormData,
            setStatusMessage,
            fetchPrompts,
            refreshAvatar: async ({ chatbotTitle, fallbackUrl }) =>
                refreshCurrentAvatarFromServer(chatbotTitle, fallbackUrl)
        });
    };

    // --- Image cropping handlers ---
    const onCropComplete = useCallback((_, croppedAreaPixelsParam) => {
        handleAvatarCropComplete({ setCroppedAreaPixels, croppedAreaPixelsParam });
    }, []);

    const applyCrop = async () => {
        await applyAvatarCrop({
            cropImageSrc,
            croppedAreaPixels,
            pendingImageFile,
            uploadAvatarAndPersist,
            setStatusMessage,
            setShowCropDialog,
            setCropImageSrc,
            setPendingImageFile,
            setCrop,
            setZoom,
            setCroppedAreaPixels
        });
    };

    const cancelCrop = () => {
        cancelAvatarCrop({
            cropImageSrc,
            pendingImageFile,
            uploadAvatarAndPersist,
            setShowCropDialog,
            setCropImageSrc,
            setPendingImageFile,
            setCrop,
            setZoom,
            setCroppedAreaPixels
        });
    };

    const confirmOcrCancelBeforeLeave = useCallback(async () => {
        return confirmOcrCancelBeforeLeaveUtil({
            ragUploadLoadingRef,
            selectedPromptTitleRef,
            ragUploadCancelHandlerRef
        });
    }, []);

    useEffect(() => {
        window.__promptEditorOcrLeaveGuard = confirmOcrCancelBeforeLeave;
        return () => {
            delete window.__promptEditorOcrLeaveGuard;
        };
    }, [confirmOcrCancelBeforeLeave]);

    const handleDeleteAvatar = () => {
        setOpenDeleteAvatarDialog(true);
    };

    const confirmDeleteAvatar = async () => {
        setOpenDeleteAvatarDialog(false);
        await clearAvatarAndPersist({
            selectedPromptTitle,
            formData,
            setFormData,
            setPendingAvatarDelete,
            fetchPrompts,
            setStatusMessage
        });
    };

    const cancelDeleteAvatar = () => {
        setOpenDeleteAvatarDialog(false);
    };

    // Load RAG documents when prompt is selected, editing mode changes, or RAG is toggled
    useEffect(() => {
        if (formData.use_rag && isEditMode && selectedPromptTitle) {
            loadRagDocumentsUtil(selectedPromptTitle, isEditMode, setRagDocuments);
        }
    }, [formData.use_rag, isEditMode, selectedPromptTitle]);

    const initializeBlankPromptForm = useCallback((language) => {
        setSelectedPromptTitle('');
        setIsEditMode(false);
        setPendingAvatarDelete(false);
        setCopiedFromChatbotTitle('');
        setCopiedFromSourceChatbotTitle('');
        setCopiedSourceHasAvatar(false);
        setFormData({
            title: generateUniqueChatbotId(),
            name: '',
            language: language || '',
            level: '',
            voice: 'alloy',
            initialText: '',
            prompt: '',
            instructions: '',
            avatar_url: '',
            use_rag: false
        });
        setStatusMessage('');
    }, []);

    // --- Handlers for Form and Dropdowns ---
    const handleLanguageChange = async (event) => {
        const canProceed = await confirmOcrCancelBeforeLeave();
        if (!canProceed) {
            return;
        }

        const lang = event.target.value;
        setSelectedLanguage(lang);

        // Reset subsequent selections and the form
        initializeBlankPromptForm(lang);
    };

    const handleSelectPrompt = async (event) => {
        const canProceed = await confirmOcrCancelBeforeLeave();
        if (!canProceed) {
            return;
        }

        const title = event.target.value;
        setPendingAvatarDelete(false); // Reset pendingAvatarDelete when switching chatbot

        if (title === CREATE_NEW_PROMPT_OPTION || !title) {
            // Use the same reset path as language selection, but keep the current language.
            initializeBlankPromptForm(selectedLanguage);
        } else if (title && allPrompts[title]) {
            setSelectedPromptTitle(title);
            setCopiedFromChatbotTitle('');
            setCopiedFromSourceChatbotTitle('');
            setCopiedSourceHasAvatar(false);
            // If an existing prompt is selected, enter edit mode and fill the form
            setIsEditMode(true);
            // Set 'voice' to the same voice as what was in formData, default to 'alloy' if missing
            const loadedPrompt = { ...allPrompts[title] };
            if (!('voice' in loadedPrompt)) {
                loadedPrompt.voice = 'alloy';
            }
            // The next two if statements ensure backward compatibility with older prompts. It can be deleted if all older prompts were updated.
            if (!('instructions' in loadedPrompt)) {
                loadedPrompt.instructions = '';
            }
            if (!('avatar_url' in loadedPrompt)) {
                loadedPrompt.avatar_url = '';
            }
            if (!('use_rag' in loadedPrompt)) {
                loadedPrompt.use_rag = false;
            }
            // Check if title is standardized, if not generate new standardized ID
            const finalTitle = isStandardizedChatbotId(title) ? title : generateUniqueChatbotId();
            setFormData({ title: finalTitle, ...loadedPrompt });
            
            // If ID was changed to standardized format, update backend immediately
            if (finalTitle !== title) {
                setStatusMessage(`Updating "${loadedPrompt.name || title}" to standardized ID...`);
                try {
                    // Save prompt with new standardized ID
                    await axios.post('/api/save_prompt', {
                        title: finalTitle,
                        ...loadedPrompt,
                        duplicate_check_exclude_title: title
                    });
                    // Delete old prompt entry
                    await axios.post('/api/delete_prompt', {
                        prompt_name: title
                    });
                    // Update selected title to new standardized ID
                    setSelectedPromptTitle(finalTitle);
                    // Refresh prompts list
                    await fetchPrompts();
                    await refreshCurrentAvatarFromServer(finalTitle, loadedPrompt.avatar_url || '');
                    setStatusMessage(`"${loadedPrompt.name || finalTitle}" ID standardized successfully`);
                } catch (error) {
                    console.error('Error updating to standardized ID:', error);
                    setStatusMessage(`Error standardizing ID: ${error.response?.data?.error || error.message}`);
                }
            } else {
                await refreshCurrentAvatarFromServer(title, loadedPrompt.avatar_url || '');
                setStatusMessage(`Editing: ${loadedPrompt.name || title}`);
            }
        }
    };

    const handleCreateNewPrompt = async () => {
        const canProceed = await confirmOcrCancelBeforeLeave();
        if (!canProceed) {
            return;
        }

        if (!selectedLanguage) {
            return;
        }

        initializeBlankPromptForm(selectedLanguage);
    };

    const applyTemplateLanguage = (templateText, language) => {
        if (!templateText) {
            return '';
        }

        const languageValue = language || '';
        return templateText
            .replace(/\[LANGUAGE\]/g, languageValue);
    };

    const handleCopyChatbot = (sourceTitle, sourceChatbotData, sourceType = 'chatbot') => {
        // Create a new unique ID for the copied chatbot
        const newTitle = generateUniqueChatbotId();

        const sourceHasRag = sourceType === 'chatbot' && Boolean(sourceChatbotData?.use_rag && sourceChatbotData?.vectorstore_path);
        const sourceHasAvatar = sourceType === 'chatbot' && Boolean(sourceChatbotData?.avatar_url);

        const copiedData = sourceType === 'template'
            ? {
                name: '',
                language: selectedLanguage || '',
                level: '',
                voice: 'alloy',
                initialText: '',
                prompt: applyTemplateLanguage(sourceChatbotData.prompt || '', selectedLanguage),
                instructions: '',
                avatar_url: '',
                use_rag: false
            }
            : {
                name: sourceChatbotData.name ? `${sourceChatbotData.name} (Copy)` : 'Copy',
                language: sourceChatbotData.language || '',
                level: sourceChatbotData.level || '',
                voice: sourceChatbotData.voice || 'alloy',
                initialText: sourceChatbotData.initialText || '',
                prompt: sourceChatbotData.prompt || '',
                instructions: sourceChatbotData.instructions || '',
                avatar_url: '',
                use_rag: false
            };
        
        // Set the form with the copied data and new title
        setFormData({
            title: newTitle,
            ...copiedData
        });

        setCopiedFromChatbotTitle(sourceHasRag ? sourceTitle : '');
        setCopiedFromSourceChatbotTitle(sourceType === 'chatbot' ? sourceTitle : '');
        setCopiedSourceHasAvatar(sourceHasAvatar);
        setPendingAvatarDelete(false);
        
        // Set to edit mode (creating new, not editing existing)
        setIsEditMode(false);
        setSelectedPromptTitle(''); // Clear the selection
        setRagDocuments([]);
        setStatusMessage(`Creating copy of "${sourceChatbotData.name || (sourceType === 'template' ? 'Template' : 'Chatbot')}"...`);
    };

    const handleFormChange = async (event) => {
        const { name, value } = event.target;

        if (name === 'level' && value === ADD_NEW_LEVEL_OPTION) {
            setNewLevelValue('');
            setOpenAddLevelDialog(true);
            return;
        }

        // Prevent moving to a level where the same chatbot name already exists.
        if (name === 'level' && isEditMode && selectedPromptTitle) {
            const trimmedName = (formData.name || '').trim().toLowerCase();
            const currentLanguage = (formData.language || selectedLanguage || '').trim().toLowerCase();
            const targetLevel = (value || '').trim().toLowerCase();
            const levelConflict = Object.entries(allPrompts).find(([key, val]) => {
                if (key === selectedPromptTitle) return false;
                const existingName = (val?.name || '').trim().toLowerCase();
                const existingLanguage = (val?.language || '').trim().toLowerCase();
                const existingLevel = (val?.level || '').trim().toLowerCase();
                return (
                    existingName
                    && existingName === trimmedName
                    && existingLanguage === currentLanguage
                    && existingLevel === targetLevel
                );
            });

            if (levelConflict) {
                setStatusMessage('Error: Another chatbot in this level already uses that name. Choose a different level or rename the chatbot first.');
                return;
            }
        }

        const nextFormData = { ...formData, [name]: value };
        setFormData(nextFormData);

        // For existing chatbots, persist level changes immediately.
        if (name === 'level' && isEditMode && selectedPromptTitle) {
            setAllPrompts((prev) => ({
                ...prev,
                [selectedPromptTitle]: {
                    ...(prev[selectedPromptTitle] || {}),
                    level: value
                }
            }));

            try {
                const submitData = { ...nextFormData, title: selectedPromptTitle };
                delete submitData.image;
                await axios.post('/api/update_prompt', submitData);
                setStatusMessage('Level updated.');
            } catch (error) {
                const errorMessage = error.response?.data?.error || error.message;
                setStatusMessage(`Error: ${errorMessage}`);
            }
        }
    };

    const handleAddNewLevel = async () => {
        const trimmedLevel = (newLevelValue || '').trim();
        if (!selectedLanguage) {
            setStatusMessage('Error: Select a language before adding a level.');
            return;
        }
        if (!trimmedLevel) {
            setStatusMessage('Error: Level name cannot be empty.');
            return;
        }

        setAddingLevel(true);
        try {
            const response = await axios.post('/api/levels/add', {
                language: selectedLanguage,
                level: trimmedLevel
            });

            await fetchAiLevels();
            const nextFormData = { ...formData, level: trimmedLevel };
            setFormData(nextFormData);

            // For existing chatbots, persist new level selection immediately.
            if (isEditMode && selectedPromptTitle) {
                setAllPrompts((prev) => ({
                    ...prev,
                    [selectedPromptTitle]: {
                        ...(prev[selectedPromptTitle] || {}),
                        level: trimmedLevel
                    }
                }));

                const submitData = { ...nextFormData, title: selectedPromptTitle };
                delete submitData.image;
                await axios.post('/api/update_prompt', submitData);
                await fetchPrompts();
            }

            setOpenAddLevelDialog(false);
            setNewLevelValue('');
            const baseMessage = response.data?.message || `Level "${trimmedLevel}" added.`;
            setStatusMessage(
                isEditMode && selectedPromptTitle
                    ? `${baseMessage} Prompt updated.`
                    : baseMessage
            );
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
            setStatusMessage(`Error: ${errorMessage}`);
        } finally {
            setAddingLevel(false);
        }
    };

    const openEditLevel = (event, level) => {
        event.preventDefault();
        event.stopPropagation();
        setLevelToEdit(level);
        setEditedLevelValue(level);
        setOpenEditLevelDialog(true);
    };

    const confirmEditLevel = async () => {
        const trimmedNewLevel = (editedLevelValue || '').trim();
        if (!selectedLanguage) {
            setStatusMessage('Error: Select a language before editing a level.');
            return;
        }
        if (!levelToEdit) {
            setStatusMessage('Error: No level selected for editing.');
            return;
        }
        if (!trimmedNewLevel) {
            setStatusMessage('Error: New level name cannot be empty.');
            return;
        }

        setEditingLevel(true);
        try {
            const response = await axios.post('/api/levels/update', {
                language: selectedLanguage,
                old_level: levelToEdit,
                new_level: trimmedNewLevel
            });

            await fetchAiLevels();
            await fetchPrompts();
            setFormData((prev) => {
                if ((prev.level || '').trim().toLowerCase() === levelToEdit.trim().toLowerCase()) {
                    return { ...prev, level: trimmedNewLevel };
                }
                return prev;
            });
            setOpenEditLevelDialog(false);
            setLevelToEdit('');
            setEditedLevelValue('');
            const updatedPrompts = Number(response.data?.updated_prompts || 0);
            const baseMessage = response.data?.message || 'Level updated successfully.';
            setStatusMessage(`${baseMessage} Updated ${updatedPrompts} prompt(s).`);
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
            setStatusMessage(`Error: ${errorMessage}`);
        } finally {
            setEditingLevel(false);
        }
    };

    const openDeleteLevel = (event, level) => {
        event.preventDefault();
        event.stopPropagation();
        setLevelToDelete(level);
        setOpenDeleteLevelDialog(true);
    };

    const confirmDeleteLevel = async () => {
        if (!selectedLanguage) {
            setStatusMessage('Error: Select a language before deleting a level.');
            return;
        }
        if (!levelToDelete) {
            setStatusMessage('Error: No level selected for deletion.');
            return;
        }

        setDeletingLevel(true);
        try {
            const response = await axios.post('/api/levels/delete', {
                language: selectedLanguage,
                level: levelToDelete
            });

            await fetchAiLevels();
            await fetchPrompts();
            setFormData((prev) => {
                if ((prev.level || '').trim().toLowerCase() === levelToDelete.trim().toLowerCase()) {
                    return { ...prev, level: '' };
                }
                return prev;
            });
            setOpenDeleteLevelDialog(false);
            setLevelToDelete('');
            const updatedPrompts = Number(response.data?.updated_prompts || 0);
            const baseMessage = response.data?.message || 'Level deleted successfully.';
            setStatusMessage(`${baseMessage} Updated ${updatedPrompts} prompt(s).`);
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message;
            setStatusMessage(`Error: ${errorMessage}`);
        } finally {
            setDeletingLevel(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setStatusMessage('Saving...');

        // Validate chatbot display name uniqueness within selected language + level (case-insensitive)
        const trimmedName = (formData.name || '').trim();
        if (!trimmedName) {
            setStatusMessage('Error: Chatbot name is required.');
            return;
        }
        const currentLanguage = (formData.language || selectedLanguage || '').trim().toLowerCase();
        const currentLevel = (formData.level || '').trim().toLowerCase();
        // If creating new prompt, any same-language+same-level name match should block.
        // If editing, allow if the matching name belongs to the same prompt title.
        const duplicateEntry = Object.entries(allPrompts).find(([key, val]) => {
            const existingName = (val?.name || '').trim();
            const existingLanguage = (val?.language || '').trim().toLowerCase();
            const existingLevel = (val?.level || '').trim().toLowerCase();
            if (!existingName) return false;
            if (existingName.toLowerCase() !== trimmedName.toLowerCase()) return false;
            if (existingLanguage !== currentLanguage) return false;
            if (existingLevel !== currentLevel) return false;
            if (isEditMode) {
                return key !== selectedPromptTitle; // duplicate if different prompt
            }
            return true; // creating new -> any match is duplicate
        });
        if (duplicateEntry) {
            setStatusMessage('Error: Another chatbot in this level already uses that name. Choose a different name.');
            return;
        }

        const url = isEditMode
            ? `/api/update_prompt` // URL for updating
            : `/api/save_prompt`;  // URL for creating

        const avatarPrepResult = await prepareAvatarForPromptSubmit({
            formData,
            setFormData,
            pendingAvatarDelete,
            setPendingAvatarDelete,
            setStatusMessage
        });
        if (!avatarPrepResult.success) {
            return;
        }

        const avatar_url = avatarPrepResult.avatar_url;
        // Always use the latest avatar_url for submitData
        const submitData = { ...formData, avatar_url };
        // Remove the image file object before sending to backend
        delete submitData.image;

        try {
            // Always use submitData (with latest avatar_url) for saving
            const response = await axios.post(url, submitData);
            setStatusMessage(`Success: ${response.data.message}`);

            if (submitData.avatar_url) {
                await refreshCurrentAvatarFromServer(submitData.title, submitData.avatar_url);
            }

            // Refresh the list of prompts after saving to ensure UI is up-to-date
            await fetchPrompts();

            if (copiedFromChatbotTitle) {
                try {
                    await axios.post(`/api/chatbot/${encodeURIComponent(submitData.title)}/clone_rag`, {
                        source_chatbot_id: copiedFromChatbotTitle
                    });
                    await fetchPrompts();
                    setSelectedPromptTitle(submitData.title);
                    setIsEditMode(true);
                    setFormData((prev) => ({
                        ...prev,
                        title: submitData.title,
                        use_rag: true
                    }));
                    await loadRagDocumentsUtil(submitData.title, true, setRagDocuments);
                    setStatusMessage(`Success: ${response.data.message} RAG data copied from the original chatbot.`);
                } catch (cloneError) {
                    console.error('Failed to clone RAG folder:', cloneError);
                    setStatusMessage(`Success: ${response.data.message} Saved, but RAG copy failed.`);
                } finally {
                    setCopiedFromChatbotTitle('');
                }
            }

            if (copiedFromSourceChatbotTitle && copiedSourceHasAvatar) {
                try {
                    const avatarCloneResponse = await axios.post(`/api/chatbot/${encodeURIComponent(submitData.title)}/clone_avatar`, {
                        source_chatbot_id: copiedFromSourceChatbotTitle
                    });
                    const clonedAvatarUrl = avatarCloneResponse?.data?.avatar_url || '';
                    if (clonedAvatarUrl) {
                        setFormData((prev) => ({
                            ...prev,
                            title: submitData.title,
                            avatar_url: clonedAvatarUrl
                        }));
                        await refreshCurrentAvatarFromServer(submitData.title, clonedAvatarUrl);
                    }
                    setStatusMessage((prev) => `${prev} Avatar copied from the original chatbot.`.trim());
                    await fetchPrompts();
                } catch (avatarCloneError) {
                    console.error('Failed to clone avatar:', avatarCloneError);
                    setStatusMessage((prev) => `${prev} Saved, but avatar copy failed.`.trim());
                } finally {
                    setCopiedFromSourceChatbotTitle('');
                    setCopiedSourceHasAvatar(false);
                }
            }

            if (copiedFromSourceChatbotTitle && !copiedSourceHasAvatar) {
                setCopiedFromSourceChatbotTitle('');
                setCopiedSourceHasAvatar(false);
            }

            // If a new prompt was created, set it as the selected item
            if (!isEditMode) {
                setSelectedPromptTitle(submitData.title);
                setIsEditMode(true);
            }
            
        } catch (error) {
            const errorMessage = error.response?.data?.error || 'An unknown error occurred.';
            console.error("Failed to save prompt:", error);
            setStatusMessage(`Error: ${errorMessage}`);
        }
    };


    // --- JSX Return ---
    return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Typography variant="h4" gutterBottom>Professor Prompt Editor</Typography>

            <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <InputLabel id="language-select-label">1. Select Language</InputLabel>
                        <Select
                            labelId="language-select-label"
                            label="1. Select Language"
                            value={selectedLanguage}
                            onChange={handleLanguageChange}
                        >
                            {availableLanguages.map((lang) => (
                                <MenuItem key={lang} value={lang}>
                                    {lang}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                    {/* The second dropdown only appears after a language is selected */}
                    {selectedLanguage && (
                        <ChatbotDropdown
                            value={selectedPromptTitle}
                            onChange={handleSelectPrompt}
                            chatbotConfigs={getFilteredPrompts()}
                            templateConfigs={allTemplates}
                            selectedLanguage={selectedLanguage}
                            searchTerm={promptSearchTerm}
                            setSearchTerm={setPromptSearchTerm}
                            disabled={false}
                            label={`2. Select Prompt to Edit or Create New`}
                            promptEditing={true}
                            onCopyChatbot={handleCopyChatbot}
                            createNewOptionValue={CREATE_NEW_PROMPT_OPTION}
                            onCreateNewPrompt={handleCreateNewPrompt}
                        />
                    )}
                </Grid>
            </Grid>

            <Paper component="form" onSubmit={handleSubmit} sx={{ p: 3, opacity: selectedLanguage ? 1 : 0.5, pointerEvents: selectedLanguage ? 'auto' : 'none' }}>
                <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>{isEditMode ? `Editing: ${formData.name}` : (selectedLanguage ? `Create New ${selectedLanguage} Prompt` : 'Select a Language to Begin')}</Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField name="name" label="Chatbot Name (e.g., Yoko Ono)" value={formData.name} onChange={handleFormChange} fullWidth required />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField name="language" label="Language" value={formData.language} onChange={handleFormChange} fullWidth required disabled />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel id="level-select-label">Level</InputLabel>
                            <Select
                                labelId="level-select-label"
                                label="Level"
                                name="level"
                                value={formData.level}
                                onChange={handleFormChange}
                                renderValue={(selected) => {
                                    if (!selected) return '';
                                    return String(selected);
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            maxHeight: 320,
                                            overflowY: 'auto'
                                        }
                                    }
                                }}
                            >
                                <MenuItem value=""><em> -- None -- </em></MenuItem>
                                <MenuItem value={ADD_NEW_LEVEL_OPTION}><em> -- Create a New Level -- </em></MenuItem>
                                
                                {getAvailableLevels().map((level) => (
                                    <MenuItem key={level} value={level}>
                                        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <Typography variant="body2">{level}</Typography>
                                            <Stack direction="row" spacing={0.5}>
                                                <IconButton
                                                    size="small"
                                                    aria-label={`edit level ${level}`}
                                                    onClick={(event) => openEditLevel(event, level)}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    aria-label={`delete level ${level}`}
                                                    onClick={(event) => openDeleteLevel(event, level)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Stack>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel id="voice-select-label">Chatbot Voice</InputLabel>
                            <Select
                                labelId="voice-select-label"
                                label="Chatbot Voice"
                                name="voice"
                                value={formData.voice}
                                onChange={handleFormChange}
                            >
                                {availableVoices.map((voice) => (
                                    <MenuItem key={voice.value} value={voice.value}>{voice.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            name="initialText"
                            label="Initial Greeting from Chatbot"
                            value={formData.initialText}
                            onChange={handleFormChange}
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={20}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            name="prompt"
                            label="System Prompt (Instructions for the AI)"
                            value={formData.prompt}
                            onChange={handleFormChange}
                            fullWidth
                            multiline
                            minRows={6}
                            maxRows={20}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            name="instructions"
                            label="Instructions for the Students"
                            value={formData.instructions}
                            onChange={handleFormChange}
                            fullWidth
                            multiline
                            minRows={4}
                            maxRows={20}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <input
                            accept="image/png, image/jpeg, .png, .jpg, .jpeg"
                            style={{ display: 'none' }}
                            id="avatar-image-upload"
                            type="file"
                            onChange={e => {
                                const file = e.target.files && e.target.files[0];
                                if (file) {
                                    // open crop dialog and let user crop or cancel (cancel will keep original file)
                                    const src = URL.createObjectURL(file);
                                    setCropImageSrc(src);
                                    setPendingImageFile(file);
                                    setShowCropDialog(true);
                                }
                            }}
                        />
                        <label htmlFor="avatar-image-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                sx={{
                                    backgroundColor: 'primary.light',
                                    borderColor: 'primary.light',
                                    color: 'background.default',
                                    '&:hover': {
                                        backgroundColor: 'primary.light',
                                        borderColor: 'primary.light'
                                    }
                                }}
                            >
                                Upload PNG or JPG as Chatbot Avatar
                            </Button>
                        </label>
                        {(!pendingAvatarDelete && formData.avatar_url) && (
                            <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                sx={{ mt: 1, display: 'block' }}
                                onClick={handleDeleteAvatar}
                            >
                                Delete Current Avatar
                            </Button>
                        )}
                        {(formData.image || formData.avatar_url) && (
                            <Box mt={2} display="flex" flexDirection="row" gap={4} alignItems="flex-start">
                                {formData.image && formData.image instanceof File ? (
                                    <Box>
                                        <Typography variant="body2">Uploaded Preview:</Typography>
                                        <img
                                            src={URL.createObjectURL(formData.image)}
                                            alt="Uploaded Preview"
                                            style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, marginTop: 8 }}
                                        />
                                    </Box>
                                ) : formData.image && (
                                    <Box>
                                        <Typography variant="body2" color="error">Image file is invalid or not supported.</Typography>
                                    </Box>
                                )}
                                {formData.avatar_url && (
                                    <Box>
                                        <Typography variant="body2">Current Avatar:</Typography>
                                        <img
                                            src={getAvatarDisplayUrl(formData.avatar_url)}
                                            alt="Chatbot Avatar"
                                            style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, marginTop: 8 }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Box
                            onClick={() => {
                                if (!isEditMode || !selectedPromptTitle) {
                                    setRagDisabledNotice(true);
                                }
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.use_rag || false}
                                        onChange={async (e) => {
                                            const isEnabled = e.target.checked;
                                            setFormData(prev => ({ ...prev, use_rag: isEnabled }));
                                            await handleRagToggleUtil(isEnabled, selectedPromptTitle, ragFileInputRef, setStatusMessage, formData);
                                            // Load documents when RAG is toggled on
                                            if (isEnabled && isEditMode && selectedPromptTitle) {
                                                await loadRagDocumentsUtil(selectedPromptTitle, isEditMode, setRagDocuments);
                                            }
                                        }}
                                        name="use_rag"
                                        color="primary"
                                        disabled={!isEditMode || !selectedPromptTitle}
                                    />
                                }
                                label="Enable additional information processing (RAG)"
                            />
                        </Box>
                        <FormHelperText sx={{ ml: 0, color: 'text.secondary' }}>
                            When enabled, this chatbot can process and answer questions based on uploaded documents, PDFs, and web pages. This increases processing time when students interact with the chatbot. <br />
                            When prompting the chatbot, use the phrase "RAG Document" when referring to the uploaded documents and webpages.
                        </FormHelperText>
                        {ragDisabledNotice && (!isEditMode || !selectedPromptTitle) && (
                            <Typography
                                variant="body2"
                                sx={{ mt: 1, color: 'error.main' }}
                            >
                                RAG is unavailable before creating the chatbot first.
                            </Typography>
                        )}
                    </Grid>

                    {/* RAG Document Management Section - Only show if RAG is enabled and chatbot is being edited */}
                    {(formData.use_rag && isEditMode && selectedPromptTitle) && (
                        <>
                            <Grid item xs={12}>
                                <Paper sx={{ p: 3, backgroundColor: 'background.paper', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                                        Manage RAG Documents
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Upload files or URLs to enhance this chatbot with knowledge. The chatbot will use these documents to answer student questions.
                                    </Typography>

                                    {/* File Upload */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                            Upload Document
                                        </Typography>
                                        <input
                                            ref={ragFileInputRef}
                                            accept=".pdf,.docx"
                                            style={{ display: 'none' }}
                                            id="rag-file-upload"
                                            type="file"
                                            onChange={async (event) => {
                                                await handleRagFileUploadUtil(
                                                    event,
                                                    selectedPromptTitle,
                                                    setRagUploadLoading,
                                                    setStatusMessage,
                                                    async () => loadRagDocumentsUtil(selectedPromptTitle, isEditMode, setRagDocuments),
                                                    setRagOcrStatusMessage,
                                                    setRagUploadCancelHandler
                                                );
                                            }}
                                            disabled={ragUploadLoading}
                                        />
                                        <label htmlFor="rag-file-upload">
                                            <Button
                                                variant="outlined"
                                                component="span"
                                                disabled={ragUploadLoading || !isEditMode}
                                                startIcon={ragUploadLoading ? <CircularProgress size={20} /> : null}
                                            >
                                                {ragUploadLoading ? 'Uploading...' : 'Choose File (PDF, DOCX)'}
                                            </Button>
                                        </label>
                                        {ragUploadLoading && ragOcrStatusMessage && (
                                            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {ragOcrStatusMessage}
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={async () => cancelRagUploadWithHandler(ragUploadCancelHandler)}
                                                    size="small"
                                                    sx={{ minWidth: 0 }}
                                                >
                                                    Cancel OCR
                                                </Button>
                                            </Box>
                                        )}
                                        <FormHelperText sx={{ mt: 1 }}>
                                            Supports: PDF and Word documents (max 50MB)
                                        </FormHelperText>
                                    </Box>

                                    {/* URL Input */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                            Add URL
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                placeholder="Enter website URL"
                                                value={ragUploadUrl}
                                                onChange={(e) => setRagUploadUrl(formatUrlForDisplay(e.target.value))}
                                                disabled={ragUrlLoading || !isEditMode}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' && !e.nativeEvent.isComposing && e.keyCode !== 229 && ragUploadUrl.trim() && !ragUrlLoading && isEditMode) {
                                                        submitRagUrlForCurrentPrompt({
                                                            ragUploadUrl,
                                                            selectedPromptTitle,
                                                            setRagUrlLoading,
                                                            setStatusMessage,
                                                            setRagUploadUrl,
                                                            loadDocuments: async () => loadRagDocumentsUtil(selectedPromptTitle, isEditMode, setRagDocuments),
                                                            selectedPromptTitleRef,
                                                            isEditModeRef,
                                                            setRagDocuments
                                                        });
                                                        e.preventDefault();
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={async () => {
                                                    await submitRagUrlForCurrentPrompt({
                                                        ragUploadUrl,
                                                        selectedPromptTitle,
                                                        setRagUrlLoading,
                                                        setStatusMessage,
                                                        setRagUploadUrl,
                                                        loadDocuments: async () => loadRagDocumentsUtil(selectedPromptTitle, isEditMode, setRagDocuments),
                                                        selectedPromptTitleRef,
                                                        isEditModeRef,
                                                        setRagDocuments
                                                    });
                                                }}
                                                disabled={ragUrlLoading || !ragUploadUrl.trim() || !isEditMode}
                                                startIcon={ragUrlLoading ? <CircularProgress size={20} /> : null}
                                            >
                                                {ragUrlLoading ? 'Processing...' : 'Add'}
                                            </Button>
                                        </Box>
                                        <FormHelperText>
                                            Add web pages to extract and store knowledge. <br />
                                            If the character count is too low, the webpage may not be processed correctly. Consider downloading and uploading as a PDF if you encounter issues.
                                        </FormHelperText>
                                    </Box>

                                    {/* Documents List */}
                                    {ragDocuments.length > 0 && (
                                        <Box sx={{ mb: 3, maxHeight: 300, overflow: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                                Documents ({ragDocuments.length})
                                            </Typography>
                                            <List dense>
                                                {ragDocuments.map((doc, idx) => {
                                                    // For files use stored_filename, for URLs use display filename
                                                    const isUrl = doc.type && doc.type.toLowerCase() === 'webpage';
                                                    const deleteId = isUrl ? doc.filename : doc.stored_filename;
                                                    const hasOcrText = !!doc.ocr_used && !!doc.ocr_text_file;
                                                    return (
                                                        <ListItem
                                                            key={deleteId}
                                                            sx={{
                                                                py: 0.5,
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                opacity: deletingDocFilename === deleteId ? 0.5 : 1
                                                            }}
                                                            secondaryAction={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    {downloadingOcrFilename === deleteId && (
                                                                        <CircularProgress size={20} />
                                                                    )}
                                                                    {deletingDocFilename === deleteId && (
                                                                        <CircularProgress size={20} />
                                                                    )}
                                                                    {hasOcrText && (
                                                                        <Button
                                                                            variant="outlined"
                                                                            size="small"
                                                                            onClick={() => handleDownloadOcrDocumentUtil({
                                                                                selectedPromptTitle,
                                                                                documentId: deleteId,
                                                                                displayFilename: doc.filename,
                                                                                setDownloadingOcrFilename,
                                                                                setStatusMessage
                                                                            })}
                                                                            disabled={!isEditMode || downloadingOcrFilename === deleteId || deletingDocFilename === deleteId}
                                                                            sx={{ minWidth: 0, px: 1 }}
                                                                        >
                                                                            Download OCR Document
                                                                        </Button>
                                                                    )}
                                                                    <IconButton
                                                                        edge="end"
                                                                        aria-label="delete"
                                                                        size="small"
                                                                        onClick={() => openDeleteRagDocumentDialog({
                                                                            storedFilename: deleteId,
                                                                            displayFilename: doc.filename,
                                                                            setDocToDelete,
                                                                            setOpenDeleteDocDialog
                                                                        })}
                                                                        disabled={!isEditMode || deletingDocFilename === deleteId || downloadingOcrFilename === deleteId}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Box>
                                                            }
                                                        >
                                                            <ListItemText
                                                                primary={doc.filename}
                                                                secondary={`${doc.type.toUpperCase()}${doc.char_count ? ` • ${doc.char_count.toLocaleString()} characters` : ''} • ${new Date(doc.processed_at).toLocaleDateString()}`}
                                                                primaryTypographyProps={{ variant: 'body2' }}
                                                                secondaryTypographyProps={{ variant: 'caption' }}
                                                            />
                                                        </ListItem>
                                                    );
                                                })}
                                            </List>
                                        </Box>
                                    )}

                                    {ragDocuments.length === 0 && (
                                        <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 3 }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                                                No documents uploaded yet. Add files or URLs to get started.
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* Clear Documents Button */}
                                    {ragDocuments.length > 0 && (
                                        <Button
                                            variant="outlined"
                                            color="error"
                                            size="small"
                                            onClick={() => openClearRagDocumentsDialog(setOpenClearDocsDialog)}
                                        >
                                            Clear All Documents
                                        </Button>
                                    )}
                                </Paper>
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12}>
                        <Button type="submit" variant="contained" color="primary" size="large">
                            {isEditMode ? 'Update Prompt' : 'Save New Prompt'}
                        </Button>

                        {/* Only show delete button if a prompt is currently selected for editing */}
                        {selectedPromptTitle && (
                            <Button
                                variant="contained"
                                color="error"
                                size="large"
                                onClick={() => handleDeletePrompt(selectedPromptTitle)}
                                sx={{ ml: 2, backgroundColor: (theme) => theme.palette.primary.dark + ' !important', color: '#fff' }}
                            >
                                Delete Prompt
                            </Button>
                        )}
                    </Grid>
                </Grid>
                {statusMessage && (
                    <Typography
                        variant="body1"
                        sx={{
                            mt: 2,
                            fontWeight: 'bold',
                            color: statusMessage.startsWith('Error') ? 'error.main' : 'success.main'
                        }}
                    >
                        {statusMessage}
                    </Typography>
                )}

                <Grid item xs={12}>
                    <Box sx={{ mb: 0.5, mt: 2 }}>
                        <Typography variant="caption" sx={{ fontSize: '0.70rem', color: 'text.secondary', fontWeight: 500 }}>
                            Unique Identifier:
                        </Typography>
                        <Typography variant="caption" sx={{ fontSize: '0.70rem', ml: 1, color: 'text.primary' }}>
                            {formData.title}
                        </Typography>
                    </Box>
                </Grid>

            </Paper>

            {/* Image cropping dialog for avatar upload */}
            <Dialog open={showCropDialog} onClose={cancelCrop} maxWidth="sm" fullWidth>
                <DialogTitle>Crop Avatar Image</DialogTitle>
                <DialogContent>
                    <Box sx={{ position: 'relative', width: '100%', height: 360, bgcolor: 'background.default' }}>
                        {cropImageSrc && (
                            <Cropper
                                image={cropImageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        )}
                    </Box>
                    <Box sx={{ mt: 2, px: 1 }}>
                        <Typography variant="body2" gutterBottom>Zoom</Typography>
                        <Slider
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.1}
                            onChange={(e, value) => setZoom(value)}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelCrop}>Cancel</Button>
                    <Button onClick={applyCrop} variant="contained">Apply Crop</Button>
                </DialogActions>
            </Dialog>

            {/* This Dialog must be within the PromptEditor's return statement */}
            <Dialog
                open={openDeleteDialog}
                onClose={cancelDelete}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Deletion"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete the chatbot: "<strong>{promptToDeleteDisplayName}</strong>"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDelete}>Cancel</Button>
                    <Button onClick={confirmDelete} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Clear RAG Documents Confirmation Dialog */}
            <Dialog
                open={openClearDocsDialog}
                onClose={() => cancelClearRagDocumentsUtil(setOpenClearDocsDialog)}
                aria-labelledby="clear-docs-dialog-title"
                aria-describedby="clear-docs-dialog-description"
            >
                <DialogTitle id="clear-docs-dialog-title">{"Confirm Clear All Documents"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="clear-docs-dialog-description">
                        Are you sure you want to clear all RAG documents for "<strong>{selectedChatbotDisplayName}</strong>"? This will delete all uploaded documents and their embeddings. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => cancelClearRagDocumentsUtil(setOpenClearDocsDialog)}>Cancel</Button>
                    <Button
                        onClick={async () => confirmClearRagDocumentsUtil({
                            selectedPromptTitle,
                            setStatusMessage,
                            loadRagDocuments: async () => loadRagDocumentsUtil(selectedPromptTitle, isEditMode, setRagDocuments),
                            setOpenClearDocsDialog
                        })}
                        color="error"
                        autoFocus
                    >
                        Clear All
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Individual Document Confirmation Dialog */}
            <Dialog
                open={openDeleteDocDialog}
                onClose={() => cancelDeleteRagDocumentUtil({ setOpenDeleteDocDialog, setDocToDelete })}
                aria-labelledby="delete-doc-dialog-title"
                aria-describedby="delete-doc-dialog-description"
            >
                <DialogTitle id="delete-doc-dialog-title">{"Confirm Delete Document"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-doc-dialog-description">
                        Are you sure you want to delete the document "<strong>{docToDelete?.displayFilename}</strong>" from chatbot "<strong>{selectedChatbotDisplayName}</strong>"? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => cancelDeleteRagDocumentUtil({ setOpenDeleteDocDialog, setDocToDelete })}>Cancel</Button>
                    <Button
                        onClick={async () => confirmDeleteRagDocumentUtil({
                            docToDelete,
                            selectedPromptTitle,
                            setOpenDeleteDocDialog,
                            setDeletingDocFilename,
                            setRagDocuments,
                            loadRagDocuments: async () => loadRagDocumentsUtil(selectedPromptTitle, isEditMode, setRagDocuments),
                            setStatusMessage,
                            setDocToDelete
                        })}
                        color="error"
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Avatar Confirmation Dialog */}
            <Dialog
                open={openDeleteAvatarDialog}
                onClose={cancelDeleteAvatar}
                aria-labelledby="delete-avatar-dialog-title"
                aria-describedby="delete-avatar-dialog-description"
            >
                <DialogTitle id="delete-avatar-dialog-title">{"Confirm Delete Avatar"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-avatar-dialog-description">
                        Are you sure you want to delete this chatbot avatar? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={cancelDeleteAvatar}>Cancel</Button>
                    <Button onClick={confirmDeleteAvatar} color="error" autoFocus>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openAddLevelDialog}
                onClose={() => {
                    if (!addingLevel) {
                        setOpenAddLevelDialog(false);
                        setNewLevelValue('');
                    }
                }}
                aria-labelledby="add-level-dialog-title"
            >
                <DialogTitle id="add-level-dialog-title">Add New Level</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Add a new level for {selectedLanguage}.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label="New Level"
                        value={newLevelValue}
                        onChange={(event) => setNewLevelValue(event.target.value)}
                        disabled={addingLevel}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.nativeEvent.isComposing && event.keyCode !== 229) {
                                event.preventDefault();
                                handleAddNewLevel();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            if (!addingLevel) {
                                setOpenAddLevelDialog(false);
                                setNewLevelValue('');
                            }
                        }}
                        disabled={addingLevel}
                    >
                        Cancel
                    </Button>
                    <Button onClick={handleAddNewLevel} variant="contained" disabled={addingLevel}>
                        {addingLevel ? 'Adding...' : 'Add Level'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openEditLevelDialog}
                onClose={() => {
                    if (!editingLevel) {
                        setOpenEditLevelDialog(false);
                        setLevelToEdit('');
                        setEditedLevelValue('');
                    }
                }}
                aria-labelledby="edit-level-dialog-title"
            >
                <DialogTitle id="edit-level-dialog-title">Edit Level</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Rename level "{levelToEdit}" for {selectedLanguage}.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Level Name"
                        value={editedLevelValue}
                        onChange={(event) => setEditedLevelValue(event.target.value)}
                        disabled={editingLevel}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' && !event.nativeEvent.isComposing && event.keyCode !== 229) {
                                event.preventDefault();
                                confirmEditLevel();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            if (!editingLevel) {
                                setOpenEditLevelDialog(false);
                                setLevelToEdit('');
                                setEditedLevelValue('');
                            }
                        }}
                        disabled={editingLevel}
                    >
                        Cancel
                    </Button>
                    <Button onClick={confirmEditLevel} variant="contained" disabled={editingLevel}>
                        {editingLevel ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openDeleteLevelDialog}
                onClose={() => {
                    if (!deletingLevel) {
                        setOpenDeleteLevelDialog(false);
                        setLevelToDelete('');
                    }
                }}
                aria-labelledby="delete-level-dialog-title"
            >
                <DialogTitle id="delete-level-dialog-title">Delete Level</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the level "{levelToDelete}" for {selectedLanguage}?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            if (!deletingLevel) {
                                setOpenDeleteLevelDialog(false);
                                setLevelToDelete('');
                            }
                        }}
                        disabled={deletingLevel}
                    >
                        Cancel
                    </Button>
                    <Button onClick={confirmDeleteLevel} color="error" variant="contained" disabled={deletingLevel}>
                        {deletingLevel ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default PromptEditor;